// Content for the Temple of Sound Distribution case study (Boost! portfolio piece)

const TOS = 'https://customer-assets.emergentagent.com/job_cymbal-quoter/artifacts';
const IMG = 'https://www.templeofsound.co.uk/images/opt';

export const TS_IMAGES = {
  hero: `${TOS}/ha7oi3lb_Epic%20BG.jpg`,
  portal: `${TOS}/e7m2rwa3_image.png`,
  series1: `${IMG}/opulent.webp`,
  series2: `${IMG}/helios.webp`,
  series3: `${IMG}/eclipse.webp`,
  goldDust: `${IMG}/gold_dust.webp`,
  beatTokens: `${IMG}/beat_tokens.webp`,
};

export const BOOST_LOGO =
  'https://customer-assets-lqy194kg.emergentagent.net/job_31bcde7c-d98a-4c27-a3ab-d96f08806001/artifacts/e3mvklhv_Logo%20Pink.png';

export const TS_HERO = {
  eyebrow: 'Boost! · Case Study',
  chips: ['Web Design', 'Full-Stack Build', 'Ecommerce', 'B2B Portal'],
  year: '2024–2025',
  title: 'Two legends, one house',
  sub:
    'A ground-up, 100% bespoke retail platform for Temple of Sound — home of London Drumstick Company and Alchemist Cymbals. No templates. No plugins. No compromises.',
  client: 'Temple of Sound Distribution',
  liveUrl: 'https://www.templeofsound.co.uk',
};

export const TS_CLIENT = {
  kicker: '01 — The Client',
  title: 'The UK\u2019s premier drum-product distributor',
  copy: [
    'Temple of Sound is the home of two revered percussion brands — London Drumstick Company and Alchemist Cymbals — serving drummers, artists and retailers across the world from London.',
    'They needed a single destination that could sell thousands of handcrafted products direct to players, run a serious wholesale operation, and reward a passionate community — all with the drama their gear deserves.',
  ],
  facts: [
    { value: 'Est. 2010', label: 'London, UK' },
    { value: '2 brands', label: 'Cymbals + Drumsticks' },
    { value: '200+', label: 'Retailers & artists' },
    { value: 'Worldwide', label: 'Shipping & trade' },
  ],
};

export const TS_CHALLENGE = {
  kicker: '02 — The Challenge',
  title: 'Two brands, two audiences, one ambitious platform',
  items: [
    {
      icon: 'Boxes',
      title: 'Thousands of products',
      copy: 'Two distinct catalogues — 15K+ cymbals and 14K+ drumstick configurations — needed to live under one roof without feeling overwhelming.',
    },
    {
      icon: 'Building2',
      title: 'B2C and B2B at once',
      copy: 'A polished direct-to-player shop and a serious wholesale operation, each with completely different pricing and journeys.',
    },
    {
      icon: 'HeartHandshake',
      title: 'A community to reward',
      copy: 'A loyal following of players and artists who deserved a rewards experience as bespoke as the gear itself.',
    },
    {
      icon: 'Ban',
      title: 'No off-the-shelf fit',
      copy: 'Nothing on the market could handle custom product builders, dual loyalty currencies and trade quoting together.',
    },
  ],
};

export const TS_APPROACH = {
  kicker: '03 — Our Approach',
  title: 'Build it all bespoke — engineered to perform and to impress',
  items: [
    {
      icon: 'Palette',
      title: 'Cinematic brand-led design',
      copy: 'Gold-on-black for Alchemist, green for London Drumstick — dramatic, animated and unmistakably premium.',
    },
    {
      icon: 'Layers',
      title: 'One platform, two brands',
      copy: 'A unified architecture that lets both brands shine while sharing cart, accounts and rewards.',
    },
    {
      icon: 'Wand2',
      title: 'Interactive product tools',
      copy: 'A custom drumstick builder and curated cymbal packs turn browsing into a creative experience.',
    },
    {
      icon: 'ShieldCheck',
      title: 'Commerce built to scale',
      copy: 'Stripe-powered checkout, subscriptions and trade quoting — secure, fast and ready for growth.',
    },
  ],
};

export const TS_SOLUTION = {
  kicker: '04 — The Solution',
  title: 'A complete retail hub, engineered from the ground up',
  features: [
    {
      icon: 'ShoppingBag',
      title: 'Full B2C shop',
      copy: 'A complete ecommerce experience across two brands — series collections, product discovery and secure Stripe checkout for thousands of products.',
    },
    {
      icon: 'Briefcase',
      title: 'Bespoke B2B trade & artist portal',
      copy: 'Wholesale pricing, custom quoting tools, bulk ordering and account management — trusted by 200+ retailers and professional artists worldwide.',
    },
    {
      icon: 'Crown',
      title: 'Three-tier membership',
      copy: 'Free, Pro (£4.99) and VIP (£9.99) subscriptions with tiered discounts, loyalty multipliers, early drops and Stripe-powered billing.',
    },
    {
      icon: 'Coins',
      title: 'Dual loyalty engine',
      copy: 'Two custom currencies — Gold Dust for cymbals, Beat Tokens for drumsticks — with redemption tiers, multipliers and a built-in referral scheme.',
    },
    {
      icon: 'Wand2',
      title: 'Custom drumstick builder',
      copy: 'An interactive studio where players configure wood, tip, balance and finish to their exact spec — 14K+ possible combinations.',
    },
    {
      icon: 'MonitorPlay',
      title: 'Content & artist hub',
      copy: 'Featured artists, product demos, WhatsApp chat and a full FAQ knowledge base — keeping drummers engaged well beyond checkout.',
    },
  ],
  gallery: {
    label: 'Alchemist cymbal series',
    shots: [
      { src: TS_IMAGES.series1, caption: 'Opulent' },
      { src: TS_IMAGES.series2, caption: 'Helios' },
      { src: TS_IMAGES.series3, caption: 'Eclipse' },
    ],
  },
  loyalty: [
    { src: TS_IMAGES.goldDust, name: 'Gold Dust', desc: 'Alchemist cymbal rewards — earn 1 per £1, redeem for exclusive discount tiers.' },
    { src: TS_IMAGES.beatTokens, name: 'Beat Tokens', desc: 'London Drumstick rewards — a second bespoke currency with its own tiers.' },
  ],
};

export const TS_RESULTS = {
  kicker: '05 — The Results',
  title: 'A flagship platform that sells, scales and rewards',
  headline: [
    { value: '15K+', unit: '', label: 'Cymbals in the store', note: 'across 8 signature Alchemist series' },
    { value: '14K+', unit: '', label: 'Drumstick configs', note: 'built live in the custom studio' },
    { value: '2', unit: '', label: 'Loyalty currencies', note: 'Gold Dust + Beat Tokens, fully bespoke' },
    { value: '200+', unit: '', label: 'Retailers & artists', note: 'served through the trade portal' },
  ],
  groups: [
    {
      icon: 'ShoppingCart',
      title: 'Commerce',
      points: [
        'Two full catalogues unified under one cart and checkout.',
        'Stripe-powered payments, subscriptions and trade quoting.',
        'Curated cymbal packs with automatic bundle savings.',
      ],
    },
    {
      icon: 'Crown',
      title: 'Membership & loyalty',
      points: [
        'Three subscription tiers (Free, Pro, VIP) with escalating perks.',
        'Dual loyalty engine with redemption tiers and referrals.',
        'Point multipliers that reward members on every order.',
      ],
    },
    {
      icon: 'Briefcase',
      title: 'Trade & community',
      points: [
        'Dedicated B2B portal with wholesale pricing and quoting.',
        'Featured-artist showcase and endorsement pipeline.',
        'WhatsApp chat + FAQ knowledge base for instant support.',
      ],
    },
  ],
};

export const TS_TECH = [
  'React', 'Tailwind', 'FastAPI', 'MongoDB', 'Stripe',
  'Custom Loyalty Engine', 'B2B Quoting', 'Product Configurator', 'WhatsApp',
];

export const TS_TESTIMONIAL = {
  // Real pull-quote featured on the Temple of Sound website.
  quote:
    'The craftsmanship is unmatched. Every cymbal tells a story, every stick feels like an extension of my hands.',
  name: 'Toby Ventura',
  role: 'Independent / Session Drummer',
  photo: '',
};

export const TS_CTA = {
  title: "Got a brand that's outgrown its website?",
  sub: "Let's build something that works as hard as you do.",
  email: 'team@boostmarketing.agency',
  siteUrl: 'https://boostmarketing.agency/#contact',
};
